import { useEffect, useState } from 'react';
import { ApiError, call } from '../api';
import { linkProps } from '../router';

interface StatusData {
  app: { version: string; uptimeSec: number; node: string };
  checks: Record<string, { ok: boolean | null; detail: string }>;
  disk: { free: number; total: number } | null;
  iptv: { id: string; name: string; status: string | null; expiresAt: number | null; maxConnections: number; activeHere: number }[];
  sessions: { user: string; title: string; subtitle: string | null; player: string; state: string | null; local: boolean; progress: number | null; quality: string; video: string; audio: string; bandwidthKbps: number | null }[];
  sessionsError: string | null;
}

const NAMES: Record<string, string> = { plex: 'Plex', seerr: 'Seerr', tautulli: 'Tautulli', tmdb: 'TMDB', fanart: 'Fanart.tv', mdblist: 'MDBList', traktClientId: 'Trakt', ffmpeg: 'ffmpeg (Live TV)' };
const DECISION: Record<string, string> = { directplay: 'Direct Play', copy: 'Direct Stream', transcode: 'Transcode' };

function gb(n: number): string {
  return `${(n / 1024 ** 3).toFixed(n > 100 * 1024 ** 3 ? 0 : 1)} GB`;
}

function uptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d} d ${h} u` : h ? `${h} u ${m} min` : `${m} min`;
}

/** Statuspagina voor de eigenaar: werkt alles, en wie kijkt er nu? Ververst elke 15 seconden. */
export function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => call<StatusData>('/api/status')
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e: unknown) => live && setError(e instanceof ApiError && e.status === 403 ? 'Alleen de eigenaar (hoofdprofiel) kan de status bekijken.' : 'Status ophalen mislukt.'));
    void load();
    const t = window.setInterval(load, 15_000);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, []);

  return (
    <main className="page status-page">
      <header className="page-head">
        <h1>Status</h1>
        {data && <span className="subtle">tumedia {data.app.version} · draait {uptime(data.app.uptimeSec)}</span>}
        <a className="row-more" {...linkProps('/settings/system')}>Instellingen</a>
      </header>
      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {!data && !error && <p className="placeholder-row" aria-busy="true">Alles nalopen…</p>}
      {data && (
        <>
          <section className="status-block" aria-label="Nu aan het kijken">
            <h2>Nu aan het kijken {data.sessions.length > 0 && <span className="subtle">({data.sessions.length})</span>}</h2>
            {data.sessionsError && <p className="error">{data.sessionsError}</p>}
            {data.sessions.length === 0 && !data.sessionsError && <p className="subtle">Er kijkt nu niemand via Plex.</p>}
            <div className="status-sessions">
              {data.sessions.map((s, i) => (
                <div key={i} className="status-session">
                  <div className="status-session-top">
                    <strong>{s.user}</strong>
                    <span className="subtle">{s.player}{s.local ? ' · thuis' : ' · buitenshuis'}</span>
                    {s.state && <span className={`status-pill ${s.state === 'playing' ? 'ok' : ''}`}>{s.state === 'playing' ? 'speelt' : s.state === 'paused' ? 'gepauzeerd' : s.state}</span>}
                  </div>
                  <span>{s.title}</span>
                  {s.subtitle && <span className="subtle">{s.subtitle}</span>}
                  {s.progress !== null && <div className="resume-bar"><div style={{ width: `${s.progress}%` }} /></div>}
                  <div className="status-decisions">
                    {s.quality && <span className="badge">{s.quality}</span>}
                    <span className={`badge ${s.video === 'transcode' ? 'badge-warn' : ''}`}>Video: {DECISION[s.video] ?? s.video}</span>
                    <span className={`badge ${s.audio === 'transcode' ? 'badge-soft' : ''}`}>Audio: {DECISION[s.audio] ?? s.audio}</span>
                    {s.bandwidthKbps && <span className="subtle">{(s.bandwidthKbps / 1000).toFixed(1)} Mbps</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="status-block" aria-label="Koppelingen">
            <h2>Koppelingen</h2>
            <ul className="status-checks">
              {Object.entries(data.checks).map(([k, c]) => (
                <li key={k}>
                  <span className={`status-dot ${c.ok === true ? 'ok' : c.ok === false ? 'bad' : 'off'}`} aria-hidden="true" />
                  <strong>{NAMES[k] ?? k}</strong>
                  <span className="subtle">{c.detail}</span>
                </li>
              ))}
              {data.disk && (
                <li>
                  <span className={`status-dot ${data.disk.free / data.disk.total < 0.05 ? 'bad' : 'ok'}`} aria-hidden="true" />
                  <strong>Opslag (appdata)</strong>
                  <span className="subtle">{gb(data.disk.free)} vrij van {gb(data.disk.total)}</span>
                </li>
              )}
            </ul>
          </section>

          {data.iptv.length > 0 && (
            <section className="status-block" aria-label="IPTV">
              <h2>IPTV</h2>
              <ul className="status-checks">
                {data.iptv.map((a) => {
                  const days = a.expiresAt ? Math.round((a.expiresAt - Date.now()) / 86_400_000) : null;
                  const ok = (a.status ?? '').toLowerCase() === 'active' && (days === null || days > 0);
                  return (
                    <li key={a.id}>
                      <span className={`status-dot ${ok ? (days !== null && days < 14 ? 'warn' : 'ok') : 'bad'}`} aria-hidden="true" />
                      <strong>{a.name}</strong>
                      <span className="subtle">
                        {a.status ?? 'onbekend'}
                        {a.expiresAt ? ` · verloopt ${new Date(a.expiresAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}${days !== null && days < 30 ? ` (nog ${days} dagen)` : ''}` : ''}
                        {` · ${a.activeHere}/${a.maxConnections} verbindingen via tumedia`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
      <div className="footer-space" />
    </main>
  );
}
